import type { MutableRefObject } from "react";
import { debounce } from "@library/shared/utils";
import { classificationHandler } from "../../../utils/classification";
import type { FroalaEditorInstance } from "../types";

// A2 fix: Per-editor debounced save to prevent cross-instance timer conflicts
const createDebouncedSave = () => {
  let editorRef: FroalaEditorInstance | null = null;
  let refreshRef: (() => void) | null = null;

  const debouncedFn = debounce(() => {
    editorRef?.save.save();
    refreshRef?.();
  }, 500);

  return (editor: FroalaEditorInstance, refresh: () => void) => {
    editorRef = editor;
    refreshRef = refresh;
    debouncedFn();
  };
};

const debouncedSaveMap = new WeakMap<
  FroalaEditorInstance,
  ReturnType<typeof createDebouncedSave>
>();

const getEditorDebouncedSave = (editor: FroalaEditorInstance) => {
  let fn = debouncedSaveMap.get(editor);
  if (!fn) {
    fn = createDebouncedSave();
    debouncedSaveMap.set(editor, fn);
  }
  return fn;
};

export const handleAiInsertText = (
  editor: FroalaEditorInstance,
  data: { text: string; type: string },
  lastRange: MutableRefObject<Range | null>,
  refresh: () => void,
) => {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount || !lastRange.current) return;
  const { text } = data;

  const range = lastRange.current.cloneRange();

  if (
    !range ||
    !range.commonAncestorContainer ||
    !range.startContainer ||
    !range.endContainer
  ) {
    console.error("[Froala] Invalid range for AI text insert");
    return;
  }

  const noSelection = range.startOffset === range.endOffset;

  const textNode = document.createTextNode(
    text.trim() + (noSelection ? " " : ""),
  );

  try {
    range.deleteContents();
    if (range.commonAncestorContainer.nodeName === "BR") {
      const p = document.createElement("p");
      p.appendChild(textNode);
      editor.html.set(p.outerHTML);
    } else {
      range.insertNode(textNode);
    }
    range.collapse();
  } catch (error) {
    console.error("[Froala] Failed to insert text node:", error);
    return;
  }

  try {
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (error) {
    console.error("[Froala] Failed to modify selection:", error);
    return;
  }

  getEditorDebouncedSave(editor)(editor, refresh);
};

export const handleAiContent = (
  editor: FroalaEditorInstance,
  data: { content: string; selection: Selection },
  refresh: () => void,
) => {
  // Already the HTML: useTextCreation unwraps the model's { content } reply.
  editor.html.set(data.content);
  editor.selection.setAtEnd(editor.$el.get(0));
  editor.selection.restore();
  editor.save.save();
  refresh();
};

export const handleAiClassification = (
  editor: FroalaEditorInstance,
  data: string,
  lastRange: MutableRefObject<Range | null>,
  refresh: () => void,
) => {
  classificationHandler({
    editor,
    lastRange: lastRange.current,
    data,
  });
  editor.save.save();
  refresh();
};
